import React from 'react';
import { Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';

export type PlanType = 'free' | 'starter' | 'professional' | 'enterprise';

interface Plan {
  id: PlanType;
  name: string;
  description: string;
  price: string;
  features: string[];
  recommended?: boolean;
}

const plans: Plan[] = [
  {
    id: 'free',
    name: 'Free',
    description: 'For small teams getting started',
    price: '$0',
    features: [
      'Up to 5 users',
      'Basic reporting',
      'Email support',
      '1GB storage',
    ],
  },
  {
    id: 'starter',
    name: 'Starter',
    description: 'For growing teams',
    price: '$29',
    features: [
      'Up to 25 users',
      'Advanced reporting',
      'Priority email support',
      '10GB storage',
      'API access',
    ],
    recommended: true,
  },
  {
    id: 'professional',
    name: 'Professional',
    description: 'For established organizations',
    price: '$99',
    features: [
      'Up to 100 users',
      'Custom reporting',
      'Phone & email support',
      '100GB storage',
      'API access',
      'Custom integrations',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'For large organizations',
    price: 'Custom',
    features: [
      'Unlimited users',
      'Enterprise reporting',
      'Dedicated support',
      'Unlimited storage',
      'Full API access',
      'Custom integrations',
      'SLA guarantee',
      'SSO/SAML',
    ],
  },
];

interface PlanSelectorProps {
  selectedPlan: PlanType;
  onSelectPlan: (plan: PlanType) => void;
}

export const PlanSelector: React.FC<PlanSelectorProps> = ({
  selectedPlan,
  onSelectPlan,
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {plans.map((plan) => {
        const isSelected = selectedPlan === plan.id;

        return (
          <Card
            key={plan.id}
            className={`cursor-pointer transition-all hover:border-primary/50 ${
              isSelected
                ? 'border-primary ring-2 ring-primary ring-offset-2'
                : ''
            } ${plan.recommended ? 'relative' : ''}`}
            onClick={() => onSelectPlan(plan.id)}
          >
            {plan.recommended && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-primary text-primary-foreground text-xs font-medium px-3 py-1 rounded-full">
                  Recommended
                </span>
              </div>
            )}
            <CardHeader className={plan.recommended ? 'pt-8' : ''}>
              <CardTitle className="flex items-center justify-between">
                <span>{plan.name}</span>
                {isSelected && (
                  <div className="h-6 w-6 rounded-full bg-primary flex items-center justify-center">
                    <Check className="h-4 w-4 text-primary-foreground" />
                  </div>
                )}
              </CardTitle>
              <CardDescription>{plan.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <span className="text-h1">{plan.price}</span>
                {plan.price !== 'Custom' && (
                  <span className="text-muted-foreground">/month</span>
                )}
              </div>
              <ul className="space-y-2">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm">
                    <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default PlanSelector;
